/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, { forwardRef } from "react";

import cn from "classnames";

import Booleanize from "utils/Booleanize";

import s from "./Toggle.module.css";

interface ToggleInterface
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value"> {
  id?: string;
  toggleSize?: "xs" | "sm" | "md" | "lg";
  value: boolean;
  onIcon?: React.ReactNode;
  offIcon?: React.ReactNode;
}

const Toggle = forwardRef(
  (
    {
      id,
      value,
      defaultValue,
      toggleSize = "lg",
      onIcon,
      offIcon,
      ...rest
    }: ToggleInterface,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const handleOnClick = (
      e: React.MouseEvent<HTMLInputElement, MouseEvent>,
    ) => {
      if (rest.onClick) rest.onClick(e);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (rest.onChange) rest.onChange(e);
    };

    return (
      <div test-id="4_VLKP5J" className="relative">
        <label
          className={cn("flex items-center relative", rest?.className)}
          test-id="GqmoCPyB"
        >
          <input
            ref={ref}
            type="checkbox"
            className={cn(
              s.hiddenInput,
              rest.disabled && "pointer-events-none",
            )}
            id={id}
            {...rest}
            onClick={handleOnClick}
            onChange={handleOnChange}
            checked={value ? Booleanize(value) : Booleanize(defaultValue)}
            test-id="ntzB6aYE"
          />

          <div
            className={cn(
              s.toggleBg,
              {
                [s.sizeXs]: toggleSize === "xs",
                [s.sizeSm]: toggleSize === "sm",
                [s.sizeMd]: toggleSize === "md",
                [s.sizeLg]: toggleSize === "lg",
              },
              " border-2 rounded-full transition-colors",
              rest.disabled && "pointer-events-none cursor-default opacity-50",
            )}
            test-id="MoDTEZ-O"
          />
        </label>

        {onIcon && (
          <div
            className={cn(
              "absolute left-1 top-0 h-full flex items-center justify-center pointer-events-none",
              !value && "hidden",
            )}
          >
            {onIcon}
          </div>
        )}

        {offIcon && (
          <div
            className={cn(
              "absolute right-1 top-0 h-full flex items-center justify-center pointer-events-none",
              value && "hidden",
            )}
          >
            {offIcon}
          </div>
        )}
      </div>
    );
  },
);

export default Toggle;
